type MerchantConfigPolicyItem = {
  default: number
  min: number
  max: number
  step: number
  editable: boolean
}

type MerchantConfigPolicy = {
  payment_window_seconds: MerchantConfigPolicyItem
  after_sales_window_seconds: MerchantConfigPolicyItem
}

const DEFAULT_MERCHANT_CONFIG_POLICY: MerchantConfigPolicy = {
  payment_window_seconds: {
    default: 900,
    min: 600,
    max: 3600,
    step: 1,
    editable: true,
  },
  after_sales_window_seconds: {
    default: 900,
    min: 900,
    max: 604800,
    step: 1,
    editable: true,
  },
}

export const DEFAULT_MERCHANT_CONFIG_POLICY_JSON = JSON.stringify(
  DEFAULT_MERCHANT_CONFIG_POLICY,
  null,
  2
)

class DuplicateJsonObjectKeyError extends Error {
  constructor(readonly key: string) {
    super(`Duplicate JSON object key: ${key}`)
    this.name = 'DuplicateJsonObjectKeyError'
  }
}

/**
 * JSON.parse silently keeps only the last duplicate object key. Scan the raw
 * JSON first so the editor never normalizes an ambiguous policy before submit.
 */
const assertNoDuplicateJsonObjectKeys = (raw: string): void => {
  let cursor = 0

  const skipWhitespace = (): void => {
    while (cursor < raw.length && /\s/u.test(raw[cursor] ?? '')) {
      cursor += 1
    }
  }

  const parseString = (): string => {
    if (raw[cursor] !== '"') {
      throw new Error('Expected JSON string')
    }

    const start = cursor
    cursor += 1
    while (cursor < raw.length) {
      const character = raw[cursor]
      if (character === '"') {
        cursor += 1
        return JSON.parse(raw.slice(start, cursor)) as string
      }
      if (character === '\\') {
        cursor += 1
        if (raw[cursor] === 'u') {
          cursor += 5
        } else {
          cursor += 1
        }
        continue
      }
      cursor += 1
    }

    throw new Error('Unterminated JSON string')
  }

  const parsePrimitive = (): void => {
    const start = cursor
    while (cursor < raw.length && !/[\s,}\]]/u.test(raw[cursor] ?? '')) {
      cursor += 1
    }
    if (cursor === start) {
      throw new Error('Expected JSON value')
    }
  }

  const parseValue = (): void => {
    skipWhitespace()
    const character = raw[cursor]
    if (character === '{') {
      cursor += 1
      skipWhitespace()
      if (raw[cursor] === '}') {
        cursor += 1
        return
      }

      const keys = new Set<string>()
      while (true) {
        skipWhitespace()
        const key = parseString()
        if (keys.has(key)) {
          throw new DuplicateJsonObjectKeyError(key)
        }
        keys.add(key)

        skipWhitespace()
        if (raw[cursor] !== ':') {
          throw new Error('Expected JSON object key separator')
        }
        cursor += 1
        parseValue()
        skipWhitespace()
        if (raw[cursor] === '}') {
          cursor += 1
          return
        }
        if (raw[cursor] !== ',') {
          throw new Error('Expected JSON object separator')
        }
        cursor += 1
      }
    }

    if (character === '[') {
      cursor += 1
      skipWhitespace()
      if (raw[cursor] === ']') {
        cursor += 1
        return
      }
      while (true) {
        parseValue()
        skipWhitespace()
        if (raw[cursor] === ']') {
          cursor += 1
          return
        }
        if (raw[cursor] !== ',') {
          throw new Error('Expected JSON array separator')
        }
        cursor += 1
      }
    }

    if (character === '"') {
      parseString()
      return
    }

    parsePrimitive()
  }

  parseValue()
  skipWhitespace()
  if (cursor !== raw.length) {
    throw new Error('Unexpected JSON content')
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const hasExactlyKeys = (
  value: Record<string, unknown>,
  expectedKeys: readonly string[]
): boolean => {
  const keys = Object.keys(value)
  return (
    keys.length === expectedKeys.length &&
    expectedKeys.every(key => Object.prototype.hasOwnProperty.call(value, key))
  )
}

const parsePositiveInteger = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`${field} must be a positive integer`)
  }
  return value
}

const parsePolicyItem = (
  value: unknown,
  field: string
): MerchantConfigPolicyItem => {
  if (!isPlainObject(value)) {
    throw new Error(`${field} must be a JSON object`)
  }
  if (!hasExactlyKeys(value, ['default', 'min', 'max', 'step', 'editable'])) {
    throw new Error(
      `${field} must define exactly default, min, max, step, and editable`
    )
  }

  const defaultValue = parsePositiveInteger(value.default, `${field}.default`)
  const min = parsePositiveInteger(value.min, `${field}.min`)
  const max = parsePositiveInteger(value.max, `${field}.max`)
  const step = parsePositiveInteger(value.step, `${field}.step`)

  if (typeof value.editable !== 'boolean') {
    throw new Error(`${field}.editable must be a boolean`)
  }
  if (min > defaultValue || defaultValue > max) {
    throw new Error(`${field}.min must be <= default <= max`)
  }
  if ((defaultValue - min) % step !== 0 || (max - min) % step !== 0) {
    throw new Error(`${field}.default and max must align to step`)
  }

  return {
    default: defaultValue,
    min,
    max,
    step,
    editable: value.editable,
  }
}

export const parseMerchantConfigPolicyJson = (value: string): string => {
  let parsed: unknown
  try {
    assertNoDuplicateJsonObjectKeys(value)
    parsed = JSON.parse(value) as unknown
  } catch (error) {
    if (error instanceof DuplicateJsonObjectKeyError) {
      throw new Error(
        'merchantConfigPolicyJson must not contain duplicate JSON object keys'
      )
    }
    throw new Error('merchantConfigPolicyJson must be valid JSON')
  }

  if (!isPlainObject(parsed)) {
    throw new Error('merchantConfigPolicyJson must be a JSON object')
  }
  if (
    !hasExactlyKeys(parsed, [
      'payment_window_seconds',
      'after_sales_window_seconds',
    ])
  ) {
    throw new Error(
      'merchantConfigPolicyJson must define exactly payment_window_seconds and after_sales_window_seconds'
    )
  }

  const policy: MerchantConfigPolicy = {
    payment_window_seconds: parsePolicyItem(
      parsed.payment_window_seconds,
      'merchantConfigPolicyJson.payment_window_seconds'
    ),
    after_sales_window_seconds: parsePolicyItem(
      parsed.after_sales_window_seconds,
      'merchantConfigPolicyJson.after_sales_window_seconds'
    ),
  }

  return JSON.stringify(policy, null, 2)
}

/**
 * Keep corrupt historical JSON visible verbatim. In particular, JSON.parse
 * would silently collapse duplicate keys before an operator can replace them.
 */
export const formatMerchantConfigPolicyJsonForEditor = (value: string): string => {
  try {
    return parseMerchantConfigPolicyJson(value)
  } catch {
    return value
  }
}
